import React, { useEffect, useMemo, useState } from "react";
import { getFoulingInformation } from "../../network/cipAPI";
import Charts from "../../FileUpload/chart";
import AgTable from "../../components/AgTable";
import PageLoading from "../../components/PageLoading";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { storeFouling } from "../../Store/TemplateStore/template.action";
import { getIndex } from "../../utils/Utils";
import { dateComparator, getSortedData } from "../../utils/tableDataFormatter";
import { useTranslation } from "react-i18next";

const FoulingIdentification = ({ activeStep }) => {
  const { t } = useTranslation();
  const [rowData, setRowData] = useState(useSelector((state) => state.fouling));
  const [foulingType, setfoulingType] = useState(
    useSelector((state) => state.foulingType)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [cleaningResponse] = useState(useSelector((state) => state.cleanData));
  const selectedTrain = useSelector((state) => state.selTrain);
  const [date] = useState(useSelector((state) => state.cleanDate));
  const cips = useSelector((state) => state.cips);
  const threshold = useState(
    useSelector((state) => state.cleaningEffectinessThreshold)
  );

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const reasonFormatter = (params) => {
    const data = params.data?.["data"];
    if (data.length === 0) {
      return t("foulingStrings.noReason");
    } else {
      const type = data[0].primary_reason;
      return foulingType ? foulingType[type] : type;
    }
  };

  const secondaryReasonFormatter = (params) => {
    const data = params.data?.["data"];
    if (data.length === 0) {
      return t("foulingStrings.noReason");
    } else {
      const type = data[0].secondary_reason;
      return foulingType ? foulingType[type] : type;
    }
  };

  const typeFormatterObject = (params) => {
    const { start_date } = params.data;
    return params.data["data"][0]["type"] === "C"
      ? t("foulingStrings.cip")
      : start_date
      ? t("foulingStrings.shutdown")
      : "-";
  };

  const dateFormatterStartDate = (params) => {
    try {
      const startDate = params.data["start_date"];
      if (startDate) {
        return moment(startDate).format("DD/MM/YYYY");
      }
      return t("ValueStrings.inprogress");
    } catch (err) {
      console.log("Error is " + err);
      return "-";
    }
  };

  const dateFormatterEndDate = (params) => {
    try {
      const end_date = params.data["end_date"];
      if (end_date) {
        return moment(end_date).format("DD/MM/YYYY");
      }
      return "-";
    } catch (err) {
      console.log("Error is " + err);
      return "-";
    }
  };

  const dispatch = useDispatch();

  const colDefs = [
    {
      headerName: t("FoulingTableStrings.index"),
      valueGetter: (params) => getIndex(params, params.data.start_date),
    },
    {
      headerName: t("FoulingTableStrings.type"),
      field: "type",
      valueFormatter: typeFormatterObject,
      valueGetter: typeFormatterObject,
    },
    {
      headerName: t("FoulingTableStrings.start_date"),
      field: "start_date",
      sortable: true,
      flex: 1,
      valueFormatter: dateFormatterStartDate,
      comparator: dateComparator,
    },
    {
      headerName: t("FoulingTableStrings.end_date"),
      field: "end_date",
      sortable: true,
      flex: 1,
      valueFormatter: dateFormatterEndDate,
      comparator: dateComparator,
    },
    {
      headerName: t("FoulingTableStrings.primary_reason"),
      field: "data",
      sortable: true,
      flex: 1,
      valueFormatter: reasonFormatter,
      valueGetter: reasonFormatter,
    },
    {
      headerName: t("FoulingTableStrings.secondary_reason"),
      field: "data",
      sortable: true,
      flex: 1,
      valueFormatter: secondaryReasonFormatter,
      valueGetter: secondaryReasonFormatter,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(!rowData);
        const res = await getFoulingInformation(selectedTrain);
        setIsLoading(false);
        if (res && res.status === 200 && res?.data) {
          if (res?.data.fouling) {
            const data = JSON.parse(res?.data.fouling);
            const groupedData = [];
            for (const key in data.cip) {
              if (data.cip.hasOwnProperty(key)) {
                const date = data.cip[key]["sd"];
                // Check if the date exists in groupedData
                const existingDateIndex = groupedData.findIndex(
                  (group) => group.date === date
                );

                // If the date is found, add the data to the existing group
                if (existingDateIndex !== -1) {
                  groupedData[existingDateIndex].data.push({
                    type: data.cip[key]["type"],
                    start_date: data.cip[key]["sd"],
                    end_date: data.cip[key]["ed"],
                    primary_reason: data.primary_reason[key],
                    secondary_reason: data.secondary_reason[key],
                  });
                } else {
                  // If the date is not found, create a new group

                  groupedData.push({
                    date,
                    start_date: data.cip[key]["sd"],
                    end_date: data.cip[key]["ed"],
                    data: [
                      {
                        type: data.cip[key]["type"],
                        start_date: data.cip[key]["sd"],
                        end_date: data.cip[key]["ed"],
                        primary_reason: data.primary_reason[key],
                        secondary_reason: data.secondary_reason[key],
                      },
                    ],
                  });
                }
              }
            }
            const sortedData = getSortedData(groupedData);
            setRowData(sortedData);
            dispatch(storeFouling(sortedData));
          } else {
            setRowData([]);
          }
        } else if (res && res.status === 422) {
          setRowData([]);
        }
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      <div className="descriptionText marginTop10 marginBtm10">
        {t("foulingStrings.foulingDescription")}
      </div>
      <div className="chart">
        <Charts
          cips={cips}
          date={date}
          cleaningResponse={cleaningResponse}
          threshold={threshold}
          activeStep={activeStep}
        />
      </div>
      {isLoading || !rowData ? (
        <PageLoading customHeight={"320px"} />
      ) : (
        <AgTable
          columnDefs={colDefs}
          rowData={rowData}
          name={`${t("excelSheetNames.fouling")}`}
        />
      )}
      <div className="RO-disclaimer">{t("disclaimer", { currentYear })}</div>
    </div>
  );
};

export default FoulingIdentification;
